// imports section Start
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { CASHIER_API_04, CASHIER_API_05 } from "../../api/ApiModel";
import { CON_STATUS_ACTIVE, CON_STATUS_NONE } from "../../utils/sys/Constants";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Breadcrumbs,
  Link,
  AppBar,
  Checkbox,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  capitalize,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Navbar from "../../components/common/Navbar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { validateCashFields } from "../../utils/common/Validations";
// imports section End

function CashBalance() {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const [cashBalances, setCashBalances] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState("");
  const [types, setTypes] = useState([]);
  const [initialCashFloat, setInitialCashFloat] = useState("0.00");
  const [finalCashAmount, setFinalCashAmount] = useState("0.00");
  const [cashAmount, setCashAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });
  // Hooks end
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    message: "",
  });
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, STOREDATE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  // Handlers start
  const handleCashAmountChange = (e) => {
    const inputValue = e.target.value;
    const validatedValue = validateCashFields(inputValue);

    if (validatedValue !== null) {
      setCashAmount(validatedValue); // Update state only if the input is valid
    }
  };

  const getTypeName = (code) => {
    const type = types.find((t) => t.CBTYPECODE === code);
    return type ? type.CBTYPE : ""; // Return the name if found, else return empty string
  };

  // Assume cashBalances array comes from API response
  const balance = cashBalances.find((item) => item.CBTYPE === "Initial") || {}; // Get the Initial balance

  // Expected Amount Calculation
  const expectedAmount = useMemo(() => {
    if (selectedTypes && getTypeName(selectedTypes) === "Initial") {
      const initialCashAmount = parseFloat(initialCashFloat || 0); // Ensure valid number
      const salesAmount = parseFloat(balance.SALESAMOUNT || 0); // Ensure valid number
      return initialCashAmount + salesAmount; // Sum the initial cash and sales amount
    }
    return 0; // Return 0 if selected type is not "Initial"
  }, [selectedTypes, initialCashFloat, balance.SALESAMOUNT]); // We depend on selectedTypes, initialCashFloat, and balance.SALESAMOUNT

  // Actual Amount Calculation
  const actualAmount = useMemo(() => {
    if (selectedTypes && getTypeName(selectedTypes) === "Final") {
      const finalBalance = cashBalances.find((item) => item.CBTYPE === "Final");
      const cashAmount = parseFloat(finalBalance?.CASHAMOUNT || 0); // Ensure valid number from the Final balance
      return cashAmount;
    }
    return 0; // Return 0 if selected type is not "Final"
  }, [selectedTypes, cashBalances]); // We depend on selectedTypes and cashBalances

  // Difference Calculation

  const difference = useMemo(() => {
    const parsedActual = parseFloat(actualAmount) || 0; // Parse and default to 0 if invalid
    const parsedExpected = parseFloat(expectedAmount) || 0; // Parse and default to 0 if invalid
    const diff = parsedExpected - parsedActual; // Reverse the order: Expected - Actual
    return diff.toFixed(2); // Return formatted result
  }, [expectedAmount, actualAmount]); // We depend on expectedAmount and actualAmount

  useEffect(() => {
    fetchCashBalance();
    fetchCashBalanceTypes();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2, // Ensures two decimal points
      maximumFractionDigits: 2, // Prevents rounding beyond two decimal points
    }).format(price);
  };

  const safeToFixed2 = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };

  // Handlers end

  const closeDuplicateDialog = () => {
    setDuplicateDialog({
      open: false,
      message: "",
    });
  };

  const handleDuplicateOK = () => {
    closeDuplicateDialog();
    saveDailyCashBalance();
  };
  // API calls start
  const fetchCashBalance = async () => {
    const params = {
      COMPCODE,
      LOCCODE,
      RESTCODE,
      RESDATE: STOREDATE,
      ENUSER: USERNAME,
      STS: CON_STATUS_ACTIVE,
    };
    try {
      const response = await externalgetApi(CASHIER_API_04, "GET", params);
      setCashBalances(response);
      console.log("cash balance respons", response);
      // Set Initial Cash Float if the response contains Initial type
      const initialBalance = response.find(
        (balance) => balance.CBTYPE === "Initial"
      );
      if (initialBalance) {
        setInitialCashFloat(initialBalance.CASHAMOUNT?.toFixed(2) || "0.00");
      }
      const finalBalance = response.find(
        (balance) => balance.CBTYPE === "Final"
      );
      if (finalBalance) {
        setFinalCashAmount(finalBalance.CASHAMOUNT?.toFixed(2) || "0.00");
      }
    } catch (error) {
      setError(error);
    }
  };

  const fetchCashBalanceTypes = async () => {
    try {
      const params = { STS: CON_STATUS_ACTIVE };
      const response = await externalgetApi(CASHIER_API_05, "GET", params);
      setTypes(response);
      setDataLoaded(true);
      const initialType = response.find((type) => type.CBTYPE === "Initial");
      if (initialType) {
        setSelectedTypes(initialType.CBTYPECODE);
      }
    } catch (error) {
      console.error("Error fetching Cash Balance Types:", error);
      setError(error);
    }
  };

  const saveDailyCashBalance = async () => {
    const selectedBalance = cashBalances.find(
      (balance) => balance.CBTYPECODE === selectedTypes
    );

    const payload = {
      dailycashcode: CON_STATUS_NONE,
      compcode: COMPCODE,
      loccode: LOCCODE,
      restcode: RESTCODE,
      cbtype: selectedTypes.toString(),
      salesamount: selectedBalance?.SALESAMOUNT.toString() || "0",
      cashamount: cashAmount.toString(),
      depositdate: STOREDATE,
      descrip: remark.toString(),
      enuser: USERNAME,
      sts: CON_STATUS_ACTIVE,
    };
    console.log("handleDailyCashBalance", payload);
    try {
      const response = await externalpostApi(CASHIER_API_04, payload);
      console.log("handleDailyCashBalance", response);
      fetchCashBalanceTypes();
      fetchCashBalance();
      setCashAmount("");
      setRemark("");
    } catch (error) {
      console.error("Error handleDailyCashBalance:", error);
      setSnackbar({
        open: true,
        message: "Failed to save cash balance. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDailyCashBalance = async (event) => {
    event?.preventDefault?.();

    const cashAmtNum = parseFloat(cashAmount);
    if (!Number.isFinite(cashAmtNum)) {
      setSnackbar({
        open: true,
        message: "Please enter cash amount.",
        severity: "warning",
      });
      return;
    }

    if (getTypeName(selectedTypes) === "Initial") {
      const previousInitial = cashBalances.find(
        (item) => item.CBTYPE === "Initial"
      );

      if (previousInitial) {
        setDuplicateDialog({
          open: true,
          message: `You have previously entered ${safeToFixed2(
            previousInitial.CASHAMOUNT
          )} as the Initial Value.`,
        });
        return;
      }
    }

    await saveDailyCashBalance();
  };
  // API calls end

  return (
    <>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                // position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Sales</Link>
                <Link underline="none">Cash Balance</Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.cb_box_container}>
        <Grid
          container
          spacing={0}
          // display={"flex"}
          // justifyItems={"space-between"}
        >
          {/* <Grid item xs={12}>
            <Box sx={styles.col_title}>
              <Typography variant="title" align="left">
                Cash Balance
              </Typography>
            </Box>
          </Grid> */}

          <Grid item xs={12} sm={12} md={6} lg={6} xl={6} px={2}>
            <Box sx={{ ...styles.cb_col_1, minHeight: "45vh" }} p={2}>
              <Box sx={styles.cb_col_title}>
                <Typography variant="body1" fontWeight={600} align="left">
                  Daily Cash Balance
                </Typography>
              </Box>
              <Box pl={2} pr={2}>
                <Box
                  mb={2}
                  mt={2}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Daily Cash Sale
                  </Typography>
                  <Typography
                    variant="title"
                    align="left"
                    color={theme.palette.system.info}
                  >
                    {formatPrice(safeToFixed2(balance.SALESAMOUNT))}
                  </Typography>
                </Box>

                <Box
                  mb={2}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Refund Total
                  </Typography>
                  <Typography
                    variant="title"
                    align="left"
                    color={theme.palette.system.danger}
                  >
                   ({formatPrice(safeToFixed2(balance.REFUNDAMOUNT))})
                  </Typography>
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
                  <InputLabel id="demo-simple-select-label">Type</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Type"
                    value={selectedTypes}
                    onChange={(e) => setSelectedTypes(e.target.value)}
                    disabled={!dataLoaded}
                  >
                    {types.map((option) => (
                      <MenuItem
                        key={option.CBTYPECODE}
                        value={option.CBTYPECODE}
                      >
                        {option.CBTYPE}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  id="outlined-basic"
                  label="Cash Amount"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                  value={cashAmount}
                  onChange={handleCashAmountChange}
                />
                <TextField
                  fullWidth
                  id="outlined-basic"
                  label="Remark"
                  variant="outlined"
                  size="small"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
                <Box display={"flex"} justifyContent={"flex-end"} pt={2}>
                  <Button
                    variant="contained"
                    onClick={handleDailyCashBalance}
                    sx={{ ...styles.btn_primary, width: "120px" }}
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
            // ml={2}
            px={2}
            sx={{ mt: { xs: 2, sm: 2, md: 0, lg: 0, xl: 0 } }}
          >
            <Box sx={{ ...styles.cb_col_1, minHeight: "45vh",  }} p={2}>
              <Box sx={styles.cb_col_title} mb={6}>
                <Typography variant="body1" fontWeight={600} align="left">
                  Daily Cash Balance Details
                </Typography>
              </Box>
              <Box pl={2} pr={2}>
                {/* Initial Cash Float */}
                <Box
                  mb={2}
                  mt={2}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Initial Cash Float:
                  </Typography>
                  <Typography variant="title" align="left">
                    {formatPrice(initialCashFloat)}
                  </Typography>
                </Box>

                <Box
                  mb={2}
                  mt={3}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Daily Cash Sale:
                  </Typography>
                  <Typography variant="title" align="left">
                    {formatPrice(safeToFixed2(balance.SALESAMOUNT))}
                  </Typography>
                </Box>

                <Box
                  mb={2}
                  mt={3}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Refund Total:
                  </Typography>
                  <Typography
                    variant="title"
                    align="left"
                  >
                    ({formatPrice(safeToFixed2(balance.REFUNDAMOUNT))})
                  </Typography>
                </Box>

                {/* Expected */}
                <Box
                  mb={2}
                  mt={3}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                   Net Transaction Value:
                  </Typography>
                  <Typography variant="title" align="left">
                    {formatPrice(
                      (
                        parseFloat(initialCashFloat || "0") +
                        parseFloat(safeToFixed2(balance.SALESAMOUNT)) -
                        parseFloat(safeToFixed2(balance.REFUNDAMOUNT))
                      ).toFixed(2)
                    )}
                  </Typography>
                </Box>

                {/* Actual */}
                <Box
                  mb={2}
                  mt={3}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                    Actual:
                  </Typography>
                  <Typography variant="title" align="left">
                    {formatPrice(finalCashAmount)}
                  </Typography>
                </Box>

                {/* Difference */}
                <Box
                  mb={2}
                  mt={3}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{
                    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
                  }}
                >
                  <Typography variant="sub_title" align="left">
                   Difference (Surplus/Deficit):
                  </Typography>
                  <Typography variant="title" align="left">
                    {formatPrice(
                      finalCashAmount -
                        (
                          parseFloat(initialCashFloat || "0") +
                          parseFloat(safeToFixed2(balance.SALESAMOUNT)) -
                          parseFloat(safeToFixed2(balance.REFUNDAMOUNT))
                        ).toFixed(2)
                    )}
                  </Typography>
                </Box>
              </Box>

              <Divider />
              {cashBalances.map((balance) => (
                <Grid
                  container
                  p={2}
                  key={balance.CBTYPECODE}
                  borderBottom={1}
                  borderColor={theme.palette.stroke.main}
                >
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    lg={4}
                    xl={4}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                    >
                      {balance.ENUSER}
                    </Typography>
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                    >
                      {balance.ENDATE}
                    </Typography>
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                    >
                      {/* {getTypeName(balance.CBTYPE)} */}
                      {balance.CBTYPE} | {balance.DESCRIP}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={4}
                    lg={4}
                    xl={4}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                      color={theme.palette.system.info}
                    >
                      Cash Sale
                    </Typography>
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                    >
                      {formatPrice(safeToFixed2(balance.SALESAMOUNT))}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    md={4}
                    lg={4}
                    xl={4}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                      color={theme.palette.system.info}
                    >
                      Entered Amount
                    </Typography>
                    <Typography
                      variant="sub_title"
                      fontWeight={500}
                      align="left"
                    >
                      {formatPrice(balance.CASHAMOUNT?.toFixed(2) || "0.00")}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
              <Divider />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={duplicateDialog.open}
        onClose={closeDuplicateDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Previous Cash Balance</DialogTitle>
        <DialogContent>
          <DialogContentText>{duplicateDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button   variant="outlined"
            sx={styles.AI_button1} onClick={closeDuplicateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDuplicateOK}
            sx={styles.AI_button2}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

export default CashBalance;
